import { ThunkDispatch } from "@reduxjs/toolkit";
import { Tooltip } from 'primereact/tooltip';
import { Modal } from "flowbite-react";
import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from "react-redux";

import { PlanTypeExtension } from '../../constants';
import { imageEditingActions, ImageEditingState, processBlueSkyImage, processMosaicImage } from '../../reducers/imageEditing';
import { toastActions } from '../../reducers/toast';
import { InfoIcon, CloseModalIcon, ConfirmGenerateAfterPhotoIcon } from '../SVGIcons';
import { HintPopup } from '../common/HintPopup';
import { AIProcessingPopup } from '../common/AIProcessingPopup';
import blueSkyImage from '../../../assets/images/pho_blue_sky.png';
import mosaicImage from '../../../assets/images/pho_mosaic.png';

// State型定義
interface RootState {
  imageEditing: ImageEditingState;
  currentUser: {
    creditConsumed: number;
    creditLimit: number;
    isLimitInfinity: boolean;
    planType?: string;
    [key: string]: unknown;
  };
  [key: string]: any;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const selectedImage = state.imageEditing.selectedImage;
  const aiProcessingUsageByImage = state.imageEditing.aiProcessingUsageByImage;
  const aiProcessingMaxCount = state.imageEditing.aiProcessingMaxCount;

  // 選択されている画像の使用回数を取得（未使用の場合は最大回数を残り回数として設定）
  const imageUsage = selectedImage && aiProcessingUsageByImage[selectedImage.id];
  const aiProcessingRemainingCount = imageUsage
    ? imageUsage.remainingCount
    : aiProcessingMaxCount;

  return {
    processingMethod: state.imageEditing.processingMethod,
    aiProcessing: state.imageEditing.aiProcessing,
    isAIProcessing: state.imageEditing.isAIProcessing,
    selectedImage,
    aiProcessingRemainingCount,
    aiProcessingMaxCount,
    creditConsumed: state.currentUser.creditConsumed,
    creditLimit: state.currentUser.creditLimit,
    isLimitInfinity: state.currentUser.isLimitInfinity,
    planType: state.currentUser.planType,
  };
}

interface ImageEditingAIPanelProps {
  processingMethod: 'single' | 'individual';
  aiProcessing: ImageEditingState['aiProcessing'];
  isAIProcessing: boolean;
  selectedImage: ImageEditingState['selectedImage'];
  aiProcessingRemainingCount: number;
  aiProcessingMaxCount: number;
  canUseAIProcessing?: boolean;
  creditConsumed: number;
  creditLimit: number;
  isLimitInfinity: boolean;
  planType?: string;
}

const ImageEditingAIPanel: React.FC<ImageEditingAIPanelProps> = (props) => {
  const { processingMethod, aiProcessing, isAIProcessing, selectedImage, aiProcessingRemainingCount, aiProcessingMaxCount, canUseAIProcessing = true, creditConsumed, creditLimit, isLimitInfinity, planType } = props;
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // Mini(画像編集)プランかどうか
  const isMiniEditPlan = PlanTypeExtension.isMiniEdit(planType);

  // クレジット確認ポップアップの表示状態
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);

  // ヒントポップアップの表示状態
  const [showBlueSkyHint, setShowBlueSkyHint] = useState(false);
  const [showMosaicHint, setShowMosaicHint] = useState(false);
  const [dontShowBlueSkyAgain, setDontShowBlueSkyAgain] = useState(false);
  const [dontShowMosaicAgain, setDontShowMosaicAgain] = useState(false);
  // ポップアップの表示元（アイコンクリック時はフラグを無視）
  const [blueSkyHintFromIcon, setBlueSkyHintFromIcon] = useState(false);
  const [mosaicHintFromIcon, setMosaicHintFromIcon] = useState(false);

  // 青空加工ヒントの「次回から表示しない」設定を確認（適用ボタンからの場合のみ）
  useEffect(() => {
    if (!blueSkyHintFromIcon) {
      const hidden = localStorage.getItem('aiBlueskyHintHidden');
      if (hidden === 'true' && showBlueSkyHint) {
        setShowBlueSkyHint(false);
      }
    }
  }, [showBlueSkyHint, blueSkyHintFromIcon]);

  // モザイクヒントの「次回から表示しない」設定を確認（適用ボタンからの場合のみ）
  useEffect(() => {
    if (!mosaicHintFromIcon) {
      const hidden = localStorage.getItem('aiMosaicHintHidden');
      if (hidden === 'true' && showMosaicHint) {
        setShowMosaicHint(false);
      }
    }
  }, [showMosaicHint, mosaicHintFromIcon]);

  // 青空加工ヒントを閉じる
  const handleCloseBlueSkyHint = () => {
    if (dontShowBlueSkyAgain) {
      localStorage.setItem('aiBlueskyHintHidden', 'true');
    }
    setShowBlueSkyHint(false);
    setDontShowBlueSkyAgain(false);
    setBlueSkyHintFromIcon(false);
  };

  // モザイクヒントを閉じる
  const handleCloseMosaicHint = () => {
    if (dontShowMosaicAgain) {
      localStorage.setItem('aiMosaicHintHidden', 'true');
    }
    setShowMosaicHint(false);
    setDontShowMosaicAgain(false);
    setMosaicHintFromIcon(false);
  };

  // AI処理タイプの変更ハンドラ
  const handleAITypeChange = (type: 'none' | 'blueSky' | 'mosaic') => {
    if (isAIProcessing) return; // 処理中は変更不可
    dispatch(imageEditingActions.setAIProcessingType(type));
  };

  // モザイク対象の切り替えハンドラ
  const handleMosaicTargetToggle = (target: 'face' | 'licensePlate') => {
    if (isAIProcessing) return; // 処理中は変更不可
    dispatch(imageEditingActions.toggleMosaicTarget(target));
  };

  // AI処理を実際に実行する関数
  const executeAIProcessing = () => {
    if (!selectedImage) return;

    if (aiProcessing.selectedType === 'blueSky') {
      dispatch(processBlueSkyImage({ imageId: selectedImage.id }));
    } else if (aiProcessing.selectedType === 'mosaic') {
      dispatch(processMosaicImage({ imageId: selectedImage.id, targets: aiProcessing.mosaicTargets }));
    }
  };

  // クレジット確認が必要な場合はポップアップを表示、不要な場合は直接実行
  const executeWithCreditCheck = () => {
    if (isMiniEditPlan) {
      setShowCreditConfirm(true);
    } else {
      executeAIProcessing();
    }
  };

  // クレジット確認ポップアップから実行
  const handleCreditConfirmExecute = () => {
    setShowCreditConfirm(false);
    executeAIProcessing();
  };

  // ヒントポップアップから実行するハンドラ
  const handleExecuteFromHint = () => {
    // ヒントポップアップを閉じる
    if (aiProcessing.selectedType === 'blueSky') {
      handleCloseBlueSkyHint();
    } else if (aiProcessing.selectedType === 'mosaic') {
      handleCloseMosaicHint();
    }

    // クレジット確認を挟んでAI処理を実行
    executeWithCreditCheck();
  };

  // AI処理実行ハンドラ
  const handleExecute = () => {
    if (isAIProcessing) return; // 処理中は実行不可

    // 選択された画像がない場合
    if (!selectedImage) {
      dispatch(toastActions.toastError('画像を選択してください'));
      return;
    }

    // AI処理タイプが選択されていない場合
    if (aiProcessing.selectedType === 'none') {
      dispatch(toastActions.toastError('AI加工タイプを選択してください'));
      return;
    }

    // モザイク処理の場合、対象が選択されているかチェック
    if (aiProcessing.selectedType === 'mosaic' && aiProcessing.mosaicTargets.length === 0) {
      dispatch(toastActions.toastError('モザイク対象を選択してください'));
      return;
    }

    // LocalStorageをチェックして、フラグがあるかどうかで分岐
    let shouldShowHint = false;
    if (aiProcessing.selectedType === 'blueSky') {
      const hidden = localStorage.getItem('aiBlueskyHintHidden');
      shouldShowHint = hidden !== 'true';
    } else if (aiProcessing.selectedType === 'mosaic') {
      const hidden = localStorage.getItem('aiMosaicHintHidden');
      shouldShowHint = hidden !== 'true';
    }

    if (shouldShowHint) {
      // ヒントポップアップを表示
      if (aiProcessing.selectedType === 'blueSky') {
        setBlueSkyHintFromIcon(false); // 適用ボタンからなのでfalse
        setShowBlueSkyHint(true);
      } else if (aiProcessing.selectedType === 'mosaic') {
        setMosaicHintFromIcon(false); // 適用ボタンからなのでfalse
        setShowMosaicHint(true);
      }
    } else {
      // ヒントを非表示にしている場合はクレジット確認を挟んで実行
      executeWithCreditCheck();
    }
  };


  return (
    <div className="bg-white rounded-lg overflow-hidden relative border-2 border-gray-300">
      <style>{`
        .orange-tooltip .p-tooltip-text {
          background-color: #f97316;
        }
        .orange-tooltip .p-tooltip-arrow {
          border-top-color: #f97316;
        }
      `}</style>
      <Tooltip target=".tooltip-ai-bluesky" className="orange-tooltip" />
      <Tooltip target=".tooltip-ai-mosaic" className="orange-tooltip" />

      {/* ヘッダー */}
      <div className="text-gray-800 text-center py-2 px-4 font-semibold bg-gray-100">
        AI加工機能
      </div>

      {/* コンテンツ部分 */}
      <div className="relative">
        {/* プラン制限オーバーレイ（ヘッダーの下のみ） */}
        {!canUseAIProcessing && (
          <div
            className="absolute inset-0 z-20 flex items-start justify-center"
            style={{ backgroundColor: 'rgba(229, 231, 235, 0.9)', paddingTop: '8px' }}
          >
            <div className="text-center text-gray-700 p-4 max-w-2xl">
              <p className="font-medium text-sm leading-relaxed">
                Basicプラン以上でご利用いただけます。<br />
                詳しくは、担当営業もしくは問合せフォームまでご連絡ください。
              </p>
            </div>
          </div>
        )}

        <div style={!canUseAIProcessing ? { opacity: 0.3 } : {}}>

      {/* メインコンテンツ */}
      <div className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* AI青空加工ボタン */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleAITypeChange('blueSky')}
              disabled={isAIProcessing || !canUseAIProcessing}
              className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                aiProcessing.selectedType === 'blueSky'
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-orange-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              AI青空加工
            </button>
            <InfoIcon
              className={`tooltip-ai-bluesky w-4 h-4 text-gray-400 ${!canUseAIProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              data-pr-tooltip="写真の空を自動で検出し、美しい青空に変換します。詳細はヒントアイコンをクリック。"
              onClick={(e) => {
                if (!canUseAIProcessing) return;
                e.stopPropagation();
                setBlueSkyHintFromIcon(true);
                setShowBlueSkyHint(true);
              }}
            />
          </div>

          {/* AI自動モザイクボタン */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleAITypeChange('mosaic')}
              disabled={isAIProcessing || !canUseAIProcessing}
              className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                aiProcessing.selectedType === 'mosaic'
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-orange-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              AI自動モザイク
            </button>
            <InfoIcon
              className={`tooltip-ai-mosaic w-4 h-4 text-gray-400 ${!canUseAIProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              data-pr-tooltip="「人の顔」や「ナンバープレート」をAIが自動で検出し、モザイク処理を行います。詳細はヒントアイコンをクリック。"
              onClick={(e) => {
                if (!canUseAIProcessing) return;
                e.stopPropagation();
                setMosaicHintFromIcon(true);
                setShowMosaicHint(true);
              }}
            />
          </div>

          {/* モザイク対象選択（モザイク選択時のみ表示） */}
          {aiProcessing.selectedType === 'mosaic' && (
            <div className="flex items-center gap-3 ml-4">
              <span className="text-sm text-gray-700 font-medium">対象:</span>

              {/* 顔チェックボックス */}
              <label className={`flex items-center ${!canUseAIProcessing || isAIProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={aiProcessing.mosaicTargets.includes('face')}
                  onChange={() => handleMosaicTargetToggle('face')}
                  disabled={isAIProcessing || !canUseAIProcessing}
                  className="sr-only"
                />
                <span
                  className="w-5 h-5 rounded border-2 mr-2 flex items-center justify-center"
                  style={aiProcessing.mosaicTargets.includes('face')
                    ? { borderColor: '#3b82f6', backgroundColor: '#3b82f6' }
                    : { borderColor: '#d1d5db' }
                  }
                >
                  {aiProcessing.mosaicTargets.includes('face') && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </span>
                <span className="text-sm text-gray-700">人の顔</span>
              </label>

              {/* ナンバープレートチェックボックス */}
              <label className={`flex items-center ${!canUseAIProcessing || isAIProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={aiProcessing.mosaicTargets.includes('licensePlate')}
                  onChange={() => handleMosaicTargetToggle('licensePlate')}
                  disabled={isAIProcessing || !canUseAIProcessing}
                  className="sr-only"
                />
                <span
                  className="w-5 h-5 rounded border-2 mr-2 flex items-center justify-center"
                  style={aiProcessing.mosaicTargets.includes('licensePlate')
                    ? { borderColor: '#3b82f6', backgroundColor: '#3b82f6' }
                    : { borderColor: '#d1d5db' }
                  }
                >
                  {aiProcessing.mosaicTargets.includes('licensePlate') && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </span>
                <span className="text-sm text-gray-700">ナンバープレート</span>
              </label>
            </div>
          )}

          {/* 残り使用回数表示 */}
          <div className="ml-auto flex items-center gap-3">
            {/* <p className="text-sm text-orange-600">
              残り使用回数: {aiProcessingRemainingCount}/{aiProcessingMaxCount}回
            </p> */}

            {/* 適用ボタン */}
            <button
              type="button"
              onClick={handleExecute}
              disabled={
                isAIProcessing ||
                !canUseAIProcessing ||
                aiProcessing.selectedType === 'none' ||
                (aiProcessing.selectedType === 'mosaic' && aiProcessing.mosaicTargets.length === 0) ||
                aiProcessingRemainingCount <= 0
              }
              className={`px-6 py-2 rounded-lg font-bold transition-colors flex items-center gap-1 ${
                !isAIProcessing && canUseAIProcessing && aiProcessing.selectedType !== 'none' &&
                (aiProcessing.selectedType !== 'mosaic' || aiProcessing.mosaicTargets.length > 0) &&
                aiProcessingRemainingCount > 0
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isAIProcessing ? '処理中...' : aiProcessingRemainingCount <= 0 ? '上限到達' : '適用'}
              {isMiniEditPlan && !isAIProcessing && aiProcessingRemainingCount > 0 && (
                <span
                  className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full"
                  style={
                    canUseAIProcessing && aiProcessing.selectedType !== 'none' &&
                    (aiProcessing.selectedType !== 'mosaic' || aiProcessing.mosaicTargets.length > 0)
                      ? { backgroundColor: '#ffd2c1', color: '#ff4500' }
                      : { backgroundColor: '#e5e7eb', color: '#9ca3af' }
                  }
                >
                  1
                </span>
              )}
            </button>
          </div>
        </div>

        {/* 青空加工ヒントポップアップ */}
        <HintPopup
          show={showBlueSkyHint}
          image={blueSkyImage}
          imageAlt="AI青空加工の説明"
          onClose={handleCloseBlueSkyHint}
          showCheckbox={!blueSkyHintFromIcon}
          checkboxValue={dontShowBlueSkyAgain}
          onCheckboxChange={setDontShowBlueSkyAgain}
          maxWidth="1000px"
          showExecuteButton={!blueSkyHintFromIcon}
          onExecute={handleExecuteFromHint}
          executeButtonText="適用"
        />

        {/* モザイクヒントポップアップ */}
        <HintPopup
          show={showMosaicHint}
          image={mosaicImage}
          imageAlt="AIモザイク処理の説明"
          onClose={handleCloseMosaicHint}
          showCheckbox={!mosaicHintFromIcon}
          checkboxValue={dontShowMosaicAgain}
          onCheckboxChange={setDontShowMosaicAgain}
          maxWidth="1000px"
          showExecuteButton={!mosaicHintFromIcon}
          onExecute={handleExecuteFromHint}
          executeButtonText="適用"
        />

        {/* AI処理中ポップアップ */}
        <AIProcessingPopup
          show={isAIProcessing}
          title="AI加工を実行中..."
          description="AIが選択された加工処理を実行しています。"
          subDescription="しばらくお待ちください..."
        />

      </div>
        </div>
      </div>

        {/* クレジット消費確認ポップアップ（Mini(画像編集)プランのみ） */}
        <Modal show={showCreditConfirm} onClose={() => setShowCreditConfirm(false)} size="md">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <button type="button" onClick={() => setShowCreditConfirm(false)}
                  className="absolute top-3 end-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center">
                  <CloseModalIcon />
                  <span className="sr-only">閉じる</span>
                </button>
                <div className="p-4 md:p-5">
                  <ConfirmGenerateAfterPhotoIcon />
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    {aiProcessing.selectedType === 'blueSky' ? 'AI青空加工' : 'AIモザイク処理'}に1回分のクレジットを使用します。よろしいですか？<br/><br/>
                  </h3>
                  <div className="flex justify-between mb-1 text-gray-500">
                    <span className="text-base font-normal">実行回数</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {isLimitInfinity
                        ? `実行回数 ${creditConsumed}回/上限なし(アカウントのクレジット上限)`
                        : `残り${creditLimit - creditConsumed}回/${creditLimit}回`
                      }
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-orange-500 h-2.5 rounded-full" style={{width: isLimitInfinity ? '0%' : `${(creditConsumed / creditLimit * 100)}%`}}></div>
                  </div>
                  <div className="flex flex-row-reverse items-center mt-6 space-x-4">
                    <button type="button" onClick={handleCreditConfirmExecute}
                      className="sm:ml-3 w-28 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">実行</button>
                    <button type="button" onClick={() => setShowCreditConfirm(false)}
                      className="sm:ml-3 w-28 py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-300 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100">キャンセル</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default connect(mapStateToProps)(ImageEditingAIPanel);
